import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GripVertical, Pencil, Plus, Ruler, Search, Trash2 } from 'lucide-react';
import {
  listMeasurements, createMeasurements, updateMeasurement, deleteMeasurement, reorderMeasurements,
} from '../api/resources';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Modal from '../components/ui/Modal';
import Field from '../components/ui/Field';
import Badge, { StatusBadge } from '../components/ui/Badge';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import EmptyState from '../components/ui/EmptyState';
import PhotoUpload from '../components/ui/PhotoUpload';
import Spinner from '../components/ui/Spinner';
import PageHeader from '../components/ui/PageHeader';

// Body zone only groups measurements on the worker's job card; it does not
// split the master list into categories.
const ZONES = [
  { value: 'UPPER', label: 'Upper Body' },
  { value: 'LOWER', label: 'Lower Body' },
  { value: 'FULL', label: 'Full / General' },
];
const zoneLabel = (z) => ZONES.find((x) => x.value === z)?.label || 'Full / General';
const blankRow = () => ({ name: '', zone: 'FULL' });

function ZoneSelect({ value, onChange, className = '' }) {
  return (
    <select className={`input ${className}`} value={value} onChange={(e) => onChange(e.target.value)} aria-label="Body zone">
      {ZONES.map((z) => <option key={z.value} value={z.value}>{z.label}</option>)}
    </select>
  );
}

export default function Measurements() {
  const qc = useQueryClient();
  const { success, error } = useToast();
  const { currentCompany } = useAuth();
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [del, setDel] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ['measurements', search], queryFn: () => listMeasurements(search) });
  const measurements = data?.measurements || [];

  const invalidate = () => qc.invalidateQueries({ queryKey: ['measurements'] });

  const delMut = useMutation({
    mutationFn: deleteMeasurement,
    onSuccess: (d) => { success(d.message); invalidate(); setDel(null); },
    onError: (e) => error(e.response?.data?.message || 'Delete failed'),
  });

  // drag & drop reorder
  const [dragId, setDragId] = useState(null);
  const onDrop = (targetId) => {
    if (dragId == null || dragId === targetId) return;
    const ids = measurements.map((m) => m.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    reorderMeasurements(ids)
      .then((r) => {
        success(r.message || `Measurements updated for ${currentCompany?.company?.name || ''}`);
        invalidate();
      })
      .catch(() => error('Could not reorder measurements.'));
  };

  const filtered = useMemo(
    () => measurements.filter((m) => m.name.toLowerCase().includes(search.toLowerCase())),
    [measurements, search],
  );

  return (
    <div>
      <PageHeader
        icon={Ruler}
        title="Measurements"
        subtitle="Standard body metrics — captured per garment on every tailoring order"
        actions={
          <>
            <div className="relative w-60">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-content-subtle" />
              <input className="input input-sm pl-8" placeholder="Search measurement…" value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <button className="btn-primary" onClick={() => setAddOpen(true)}>
              <Plus size={15} /> Add Measurement
            </button>
          </>
        }
      />

      <div className="card overflow-hidden">
        {!isLoading && filtered.length === 0 && <EmptyState title="No measurements found" hint="Add your first measurement to get started." />}
        {isLoading && <Spinner />}

        {!isLoading && filtered.length > 0 && (
          <table className="w-full">
            <thead className="bg-surface-sunken">
              <tr>
                <th className="th w-10" />
                <th className="th w-14">SR</th>
                <th className="th">Measurement Name</th>
                <th className="th">Body Zone</th>
                <th className="th">Status</th>
                <th className="th text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m, i) => (
                <tr key={m.id} className={`row ${dragId === m.id ? 'bg-brand-50' : ''}`}>
                  <td className="td cursor-grab text-center text-content-subtle" draggable onDragStart={() => setDragId(m.id)} onDragOver={(e) => e.preventDefault()} onDrop={() => onDrop(m.id)} title="Drag to reorder">
                    <GripVertical size={14} />
                  </td>
                  <td className="td text-content-subtle">{i + 1}</td>
                  <td className="td font-medium text-content">{m.name}</td>
                  <td className="td text-sm text-content-muted">{zoneLabel(m.zone)}</td>
                  <td className="td"><StatusBadge status={m.status} /></td>
                  <td className="td text-right">
                    <div className="flex items-center justify-end gap-0.5">
                      <button className="btn-icon" title="Edit" onClick={() => setEdit(m)}><Pencil size={15} /></button>
                      <button className="btn-icon hover:bg-danger-50 hover:text-danger-600" title="Delete" onClick={() => setDel(m)}><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add (bulk) modal */}
      <AddMeasurements open={addOpen} onClose={() => setAddOpen(false)} onSaved={() => { invalidate(); setAddOpen(false); }} />

      {/* Edit modal */}
      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit Measurement" size="sm">
        {edit && <EditForm m={edit} onDone={() => { invalidate(); setEdit(null); }} />}
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!del}
        onClose={() => setDel(null)}
        title="Delete Measurement"
        message="Are you sure you want to delete this measurement? This action cannot be undone."
        busy={delMut.isPending}
        onConfirm={() => delMut.mutate(del.id)}
      />
    </div>
  );
}

function AddMeasurements({ open, onClose, onSaved }) {
  const { success, error } = useToast();
  const [rows, setRows] = useState([blankRow()]);
  const mutate = useMutation({
    mutationFn: createMeasurements,
    onSuccess: (d) => { success(d.message); onSaved(); setRows([blankRow()]); },
    onError: (e) => error(e.response?.data?.message || 'Save failed'),
  });
  const setRow = (i, patch) => setRows((x) => x.map((v, j) => (j === i ? { ...v, ...patch } : v)));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add Measurements"
      size="md"
      footer={
        <>
          <button className="btn-outline" onClick={onClose}>Cancel</button>
          <button className="btn-primary" disabled={mutate.isPending} onClick={() => {
            const names = rows.map((r) => ({ name: r.name.trim(), zone: r.zone })).filter((r) => r.name);
            if (names.length) mutate.mutate(names);
          }}>
            {mutate.isPending ? 'Saving…' : 'Save All Measurements'}
          </button>
        </>
      }
    >
      <p className="mb-3 text-sm text-content-muted">Add one or more measurement names. The body zone groups them on the worker's job card.</p>
      <div className="space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <input
              className="input flex-1"
              placeholder={`Measurement name ${i + 1}`}
              value={r.name}
              onChange={(e) => setRow(i, { name: e.target.value })}
              autoFocus={i === 0}
            />
            <ZoneSelect className="w-40" value={r.zone} onChange={(zone) => setRow(i, { zone })} />
            <button
              type="button"
              onClick={() => setRows((x) => (x.length === 1 ? [blankRow()] : x.filter((_, j) => j !== i)))}
              className="btn-icon hover:bg-danger-50 hover:text-danger-600"
              title="Remove"
            >
              <Trash2 size={15} />
            </button>
          </div>
        ))}
      </div>
      <button type="button" className="mt-3 text-sm font-semibold text-brand-700 hover:underline" onClick={() => setRows((x) => [...x, blankRow()])}>
        + Add Another Row
      </button>
    </Modal>
  );
}

function EditForm({ m, onDone }) {
  const { success, error } = useToast();
  const [form, setForm] = useState({ name: m.name, image: m.image || '', displayOrder: m.displayOrder, status: m.status, zone: m.zone || 'FULL' });
  const mutate = useMutation({
    mutationFn: (body) => updateMeasurement(m.id, body),
    onSuccess: (d) => { success(d.message); onDone(); },
    onError: (e) => error(e.response?.data?.message || 'Update failed'),
  });

  return (
    <div className="space-y-4">
      <Field label="Measurement Name" required>
        <input className="input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
      </Field>
      <Field label="Body Zone" hint="Groups this measurement on the worker's job card">
        <ZoneSelect value={form.zone} onChange={(zone) => setForm((f) => ({ ...f, zone }))} />
      </Field>
      <Field label="Measurement Image">
        <PhotoUpload value={form.image} onChange={(image) => setForm((f) => ({ ...f, image }))} />
      </Field>
      <Field label="Display Order">
        <input type="number" className="input" value={form.displayOrder} onChange={(e) => setForm((f) => ({ ...f, displayOrder: e.target.value }))} />
      </Field>
      <Field label="Status">
        <div className="flex items-center gap-4">
          {['ACTIVE', 'INACTIVE'].map((s) => (
            <label key={s} className="flex items-center gap-2 text-sm text-content-muted">
              <input type="radio" className="text-brand-700" checked={form.status === s} onChange={() => setForm((f) => ({ ...f, status: s }))} />
              {s === 'ACTIVE' ? <Badge tone="green">Active</Badge> : <Badge tone="slate">Inactive</Badge>}
            </label>
          ))}
        </div>
      </Field>
      <div className="flex justify-end gap-2 pt-2">
        <button className="btn-outline" onClick={onDone}>Cancel</button>
        <button className="btn-primary" disabled={mutate.isPending || !form.name.trim()} onClick={() => mutate.mutate(form)}>
          {mutate.isPending ? 'Saving…' : 'Update Measurement'}
        </button>
      </div>
    </div>
  );
}
